<script lang="ts" module>
  // Columnas sortable. Si agregas una nueva, sumá el case en compareValues
  // del padre (Sessions.svelte).
  export type SortColumn =
    | 'sessionId'
    | 'startTime'
    | 'symbol'
    | 'accountName'
    | 'operatorId'
    | 'totalPnL'
    | 'tradeCount'
    | 'winRate'
    | 'profitFactor';
  export type SortDir = 'asc' | 'desc';
</script>

<script lang="ts">
  import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    BarChart3,
    Check,
    ChevronDown,
    GitCompare,
    Hammer,
    Loader2,
    Radio,
    Zap,
  } from 'lucide-svelte';
  import { push } from 'svelte-spa-router';
  import type { LiveClosedTrade, LiveOpenTrade } from '@rhdashboard-vnext/shared';
  import { fetchSessionTrades, type SessionTradeDoc } from '$lib/api/charts';
  import { type ParityStatus, type SessionSummary } from '$lib/api/sessions';
  import { Badge } from '$lib/components/ui/badge';
  import { Button } from '$lib/components/ui/button';
  import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
  } from '$lib/components/ui/table';
  import { Tooltip, TooltipContent, TooltipTrigger } from '$lib/components/ui/tooltip';
  import CopyableSessionId from './CopyableSessionId.svelte';
  import ParityCheckModal from './ParityCheckModal.svelte';
  import SessionDetailPanel from './SessionDetailPanel.svelte';
  import WarmupParityModal from './WarmupParityModal.svelte';

  interface Props {
    sessions: SessionSummary[];
    renkoSessionIds: Set<string>;
    parityStatuses: Map<string, ParityStatus>;
    sortBy: SortColumn;
    sortDir: SortDir;
    onSortChange?: (col: SortColumn) => void;
  }

  let {
    sessions,
    renkoSessionIds,
    parityStatuses,
    sortBy,
    sortDir,
    onSortChange,
  }: Props = $props();

  // ----- Expand row state -----
  // Una session expandida a la vez (UX limpia; multi-expand puede agregarse
  // despues si el operador lo pide).
  let expandedSessionId = $state<string | null>(null);

  interface ExpandedData {
    loading: boolean;
    error: string | null;
    trades: SessionTradeDoc[]; // todos (open + closed)
  }
  // Cache por sessionId para evitar refetch al re-expandir.
  const expandedCache = $state<Map<string, ExpandedData>>(new Map());

  async function toggleExpand(sessionId: string): Promise<void> {
    if (expandedSessionId === sessionId) {
      expandedSessionId = null;
      return;
    }
    expandedSessionId = sessionId;
    if (expandedCache.has(sessionId)) return; // ya cacheado

    expandedCache.set(sessionId, { loading: true, error: null, trades: [] });
    expandedCache.set(sessionId, expandedCache.get(sessionId)!); // trigger reactivity
    try {
      const trades = await fetchSessionTrades(sessionId, 'all');
      expandedCache.set(sessionId, { loading: false, error: null, trades });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to load trades';
      expandedCache.set(sessionId, { loading: false, error: msg, trades: [] });
    }
  }

  // Convierte SessionTradeDoc[] (shape del endpoint) a {open, closed} con el
  // shape que espera SessionDetailPanel (LiveOpenTrade / LiveClosedTrade).
  function splitTrades(trades: SessionTradeDoc[]): {
    open: LiveOpenTrade[];
    closed: LiveClosedTrade[];
  } {
    const open: LiveOpenTrade[] = [];
    const closed: LiveClosedTrade[] = [];
    for (const t of trades) {
      if (t.status === 'open') {
        open.push({
          side: t.side,
          entryTime: t.entryTime,
          entryPrice: t.entryPrice,
          quantity: t.quantity,
          stopLoss: t.stopLoss,
          takeProfit: t.takeProfit,
          orderId: t.orderId,
          valuePerPoint: t.valuePerPoint,
        });
      } else if (t.status === 'closed') {
        closed.push({
          side: t.side,
          entryTime: t.entryTime,
          exitTime: t.exitTime,
          entryPrice: t.entryPrice,
          exitPrice: t.exitPrice,
          entryTriggerPrice: t.entryTriggerPrice,
          exitTriggerPrice: t.exitTriggerPrice,
          quantity: t.quantity,
          pnl: t.pnl,
          btPnl: t.btPnl,
          closeReason: t.closeReason,
        });
      }
    }
    // Closed mas reciente primero (mismo orden que en Live Monitor).
    closed.sort((a, b) => (b.exitTime ?? '').localeCompare(a.exitTime ?? ''));
    return { open, closed };
  }

  // Calcula maxDrawdown desde los closed trades. Mismo algoritmo que el
  // pipeline del backend (running PnL + peak + dd).
  function computeMaxDrawdown(closed: LiveClosedTrade[]): number {
    // Closed viene desc, ordenamos asc para el reduce cronologico.
    const asc = [...closed].sort((a, b) =>
      (a.exitTime ?? '').localeCompare(b.exitTime ?? ''),
    );
    let running = 0;
    let peak = 0;
    let dd = 0;
    for (const t of asc) {
      running += t.pnl;
      if (running > peak) peak = running;
      const cur = running - peak; // <= 0
      if (cur < dd) dd = cur;
    }
    return dd;
  }

  // ----- Formatters -----
  function fmtPnL(n: number): string {
    if (n === 0) return '$0.00';
    const sign = n > 0 ? '+' : '-';
    return `${sign}$${Math.abs(n).toFixed(2)}`;
  }
  function fmtPF(pf: number | null): string {
    if (pf === null) return '∞';
    if (!Number.isFinite(pf)) return '—';
    return pf.toFixed(2);
  }
  function fmtStartTime(t: string | number | null): string {
    if (!t) return '—';
    const d = typeof t === 'number' ? new Date(t) : new Date(t);
    if (Number.isNaN(d.getTime())) return String(t);
    const day = d.toLocaleString('en-US', { day: '2-digit' });
    const month = d.toLocaleString('en-US', { month: 'short' });
    const time = d.toLocaleString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
    return `${day} ${month} ${time}`;
  }
  function fmtStartTimeFull(t: string | number | null): string {
    if (!t) return '';
    const d = typeof t === 'number' ? new Date(t) : new Date(t);
    if (Number.isNaN(d.getTime())) return String(t);
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  /**
   * Normaliza el accountName segun la regla del usuario:
   *   "Sim 20260508112336"  -> "SIM"
   *   "Sim Account"          -> "SIM"
   *   "Demo Main"            -> "Demo Main" (sin tocar)
   *   "Live Main"            -> "Live Main" (sin tocar)
   *   null                   -> "—"
   */
  function fmtAccount(name: string | null): string {
    if (!name) return '—';
    const trimmed = name.trim();
    if (/^sim\b/i.test(trimmed)) return 'SIM';
    return trimmed;
  }

  function fmtSymbol(s: SessionSummary): string {
    return s.timeSpan ? `${s.symbol} (${s.timeSpan})` : s.symbol;
  }

  function ariaSortFor(col: SortColumn): 'ascending' | 'descending' | 'none' {
    if (sortBy !== col) return 'none';
    return sortDir === 'asc' ? 'ascending' : 'descending';
  }

  // ============================================================
  // Mode badges - logica y colores ESPEJO de v1.
  // Doc fuente: D:/RabbytAI/01-Projects/RH_DashBoard/docs/sessions-tab-badges.md
  //
  // Orden visual (mismo que v1):
  //   1. TS (naranja)             - configSource === 'timeSlots'
  //   2. WPM (verde)               - filterWarmupToTradingHours === true
  //   3. D-WPM:N | D-WPM:Auto (purpura) - dwpmActive con label
  //   4. BSR (verde)               - enableBracketReset === true
  //   5. vNext (cyan)              - vNextRestored truthy
  //   6. WP:OK | WP:DRIFT | WP:CRITICAL  - parityStatuses[sessionId]
  // ============================================================
  interface ModeBadge {
    label: string;
    color: string;
    title: string;
    onClick?: () => void;
    // PX5 badge: ícono Zap + pulse animation. Otros badges no lo usan.
    icon?: 'zap';
    pulse?: boolean;
  }

  function modeBadgesFor(s: SessionSummary): ModeBadge[] {
    const out: ModeBadge[] = [];
    if (s.isTimeSlotSession) {
      out.push({
        label: 'TS',
        color: 'bg-primary/20 text-primary border-primary/40 hover:bg-primary/25',
        title: 'TimeSlots multi-config session',
      });
    }
    if (s.filterWarmupToTradingHours) {
      out.push({
        label: 'WPM',
        color: 'bg-success/20 text-success border-success/40 hover:bg-success/25',
        title: 'Warmup-to-trading-hours filter',
      });
    }
    if (s.dwpmActive && s.dwpmLabel) {
      out.push({
        label: `D-WPM:${s.dwpmLabel}`,
        color:
          'bg-purple-500/20 text-purple-300 border-purple-500/40 hover:bg-purple-500/25',
        title:
          s.dwpmLabel === 'Auto'
            ? 'Deterministic warmup (auto-calculated)'
            : `Deterministic warmup (${s.dwpmLabel} sessions)`,
      });
    }
    if (s.enableBracketReset) {
      out.push({
        label: 'BSR',
        color: 'bg-success/20 text-success border-success/40 hover:bg-success/25',
        title: 'Bracket Stop Reset',
      });
    }
    if (s.pollingExit) {
      out.push({
        label: 'PX5',
        // Casa cyan eléctrico, contenido (rayo + letras) en dorado para contraste fuerte.
        color: 'bg-cyan-500/20 text-yellow-400 border-cyan-500/60 hover:bg-cyan-500/30',
        title:
          'Polling exits (5s intra-candle): bot pide velas cada 5s con trade abierto para detectar stop-loss/trailing antes del cierre del minuto',
        icon: 'zap',
        pulse: true,
      });
    }
    if (s.vNextRestored) {
      out.push({
        label: 'vNext',
        color: 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40 hover:bg-cyan-500/25',
        title: 'vNext warmup restored from snapshot',
      });
    }
    const pStatus = parityStatuses.get(s.sessionId);
    if (pStatus) {
      const colorByStatus: Record<string, string> = {
        OK: 'bg-success/20 text-success border-success/40 hover:bg-success/30',
        DRIFT:
          'bg-warning/20 text-warning border-warning/40 hover:bg-warning/30',
        CRITICAL:
          'bg-destructive/20 text-destructive border-destructive/40 hover:bg-destructive/30',
      };
      out.push({
        label: `WP:${pStatus}`,
        color:
          colorByStatus[pStatus] ??
          'bg-muted text-muted-foreground border-border hover:bg-muted/80',
        title: `Warmup parity status: ${pStatus} - click to open detail`,
        onClick: () => onWarmupBadgeClick(s.sessionId),
      });
    }
    return out;
  }

  // Tooltip del symbol: muestra detalles del config solo si tienen valor != 0.
  interface ConfigTooltipRow {
    label: string;
    value: string;
  }
  function symbolTooltipRows(s: SessionSummary): ConfigTooltipRow[] {
    const rows: ConfigTooltipRow[] = [];
    if (s.timeSpan) rows.push({ label: 'TimeSpan', value: s.timeSpan });
    if (s.atrPeriod !== null) rows.push({ label: 'ATR period', value: String(s.atrPeriod) });
    if (s.boxSizeMultiplier !== null)
      rows.push({ label: 'Box × ATR', value: String(s.boxSizeMultiplier) });
    if (s.offset > 0) rows.push({ label: 'Offset', value: String(s.offset) });
    if (s.slippage > 0) rows.push({ label: 'Slippage', value: String(s.slippage) });
    return rows;
  }

  // Renko (Fase 5): navega al detail view de Chart Analysis con la session
  // pre-seleccionada (mismo URL pattern que cuando hacés click desde Live
  // Monitor o desde la lista de /charts).
  function onRenkoClick(sessionId: string): void {
    push(`/charts?s=${encodeURIComponent(sessionId)}`);
  }
  // Parity check + warmup modals (Fase 6). Controlamos abrir/cerrar desde aca:
  // los modales viven al final del template y reciben el sessionId activo.
  let parityCheckSessionId = $state<string | null>(null);
  let warmupSessionId = $state<string | null>(null);
  let parityCheckOpen = $state(false);
  let warmupOpen = $state(false);
  // Flag para mostrar "Back to Parity Check" en el WarmupParityModal cuando
  // se abre desde el badge status del check modal (no desde el badge WP:*).
  let warmupCameFromCheck = $state(false);

  function onParityClick(sessionId: string): void {
    parityCheckSessionId = sessionId;
    parityCheckOpen = true;
  }
  function onWarmupBadgeClick(sessionId: string): void {
    warmupSessionId = sessionId;
    warmupCameFromCheck = false;
    warmupOpen = true;
  }
  function onOpenWarmupFromCheck(sessionId: string): void {
    parityCheckOpen = false;
    warmupSessionId = sessionId;
    warmupCameFromCheck = true;
    warmupOpen = true;
  }
  function onBackToCheckFromWarmup(): void {
    warmupOpen = false;
    warmupCameFromCheck = false;
    parityCheckOpen = true;
  }
  function onWarmupClose(): void {
    warmupOpen = false;
    warmupCameFromCheck = false;
  }
</script>

<!-- Header sortable reusable -->
{#snippet sortHeader(col: SortColumn, label: string, align: 'left' | 'right' = 'left')}
  <TableHead class={align === 'right' ? 'text-right' : ''} aria-sort={ariaSortFor(col)}>
    <button
      type="button"
      class={[
        'hover:text-foreground inline-flex items-center gap-1 transition-colors',
        align === 'right' && 'flex-row-reverse',
        sortBy === col ? 'text-primary' : 'text-muted-foreground',
      ]
        .filter(Boolean)
        .join(' ')}
      onclick={() => onSortChange?.(col)}
    >
      {label}
      {#if sortBy === col}
        {#if sortDir === 'asc'}
          <ArrowUp class="size-3" />
        {:else}
          <ArrowDown class="size-3" />
        {/if}
      {:else}
        <ArrowUpDown class="size-3 opacity-40" />
      {/if}
    </button>
  </TableHead>
{/snippet}

<div class="rounded-md border">
  {#if sessions.length === 0}
    <p class="text-muted-foreground py-12 text-center text-sm">
      No sessions match the filters.
    </p>
  {/if}
  <Table>
    <TableHeader>
      <TableRow>
        <TableHead class="w-[36px]" aria-label="Expand"></TableHead>
        {@render sortHeader('sessionId', 'Session')}
        <TableHead class="w-[80px]">Status</TableHead>
        {@render sortHeader('symbol', 'Symbol')}
        {@render sortHeader('accountName', 'Account')}
        {@render sortHeader('operatorId', 'Operator')}
        {@render sortHeader('startTime', 'Start')}
        {@render sortHeader('totalPnL', 'P&L', 'right')}
        {@render sortHeader('tradeCount', 'Trades', 'right')}
        {@render sortHeader('winRate', 'Win %', 'right')}
        {@render sortHeader('profitFactor', 'PF', 'right')}
      </TableRow>
    </TableHeader>
    <TableBody>
      {#each sessions as s, i (`${s.sessionId}-${i}`)}
        {@const badges = modeBadgesFor(s)}
        {@const tooltipRows = symbolTooltipRows(s)}
        {@const renkoExists = renkoSessionIds.has(s.sessionId)}
        {@const isExpanded = expandedSessionId === s.sessionId}
        {@const expandedData = expandedCache.get(s.sessionId)}
        <TableRow
          class={[
            'hover:bg-accent/30 cursor-pointer transition-colors',
            isExpanded && 'bg-accent/40',
          ]
            .filter(Boolean)
            .join(' ')}
          onclick={() => void toggleExpand(s.sessionId)}
        >
          <!-- Expand toggle column -->
          <TableCell class="w-[36px] align-top">
            <ChevronDown
              class={[
                'text-muted-foreground size-4 transition-transform',
                isExpanded && 'rotate-180 text-primary',
              ]
                .filter(Boolean)
                .join(' ')}
            />
          </TableCell>
          <!-- Session ID + action buttons + mode badges -->
          <TableCell class="w-[280px] align-top">
            <div class="flex flex-col gap-1.5">
              <div class="flex items-center gap-1.5">
                <!-- ID copiable -->
                <CopyableSessionId sessionId={s.sessionId} />

                <!-- Renko: BarChart3 si existe en renkoBlocks, Hammer si no -->
                <Tooltip>
                  <TooltipTrigger>
                    {#snippet child({ props })}
                      <Button
                        {...props}
                        size="icon"
                        variant="ghost"
                        class="text-muted-foreground hover:text-primary size-6"
                        aria-label={renkoExists ? 'Open Renko chart' : 'Create Renko'}
                        onclick={(e) => {
                          e.stopPropagation();
                          onRenkoClick(s.sessionId);
                        }}
                      >
                        {#if renkoExists}
                          <BarChart3 class="size-3.5" />
                        {:else}
                          <Hammer class="size-3.5" />
                        {/if}
                      </Button>
                    {/snippet}
                  </TooltipTrigger>
                  <TooltipContent>
                    {renkoExists ? 'Open Renko chart · Phase 5' : 'Create Renko blocks · Phase 5'}
                  </TooltipContent>
                </Tooltip>

                <!-- Parity check -->
                <Tooltip>
                  <TooltipTrigger>
                    {#snippet child({ props })}
                      <Button
                        {...props}
                        size="icon"
                        variant="ghost"
                        class="text-muted-foreground hover:text-primary size-6"
                        aria-label="Run parity check"
                        onclick={(e) => {
                          e.stopPropagation();
                          onParityClick(s.sessionId);
                        }}
                      >
                        <GitCompare class="size-3.5" />
                      </Button>
                    {/snippet}
                  </TooltipTrigger>
                  <TooltipContent>Parity check · Phase 6</TooltipContent>
                </Tooltip>
              </div>

              <!-- Mode badges (TS, WPM, D-WPM, BSR, vNext, WP) -->
              {#if badges.length > 0}
                <div class="flex flex-wrap gap-1">
                  {#each badges as b (b.label)}
                    {#if b.onClick}
                      <button
                        type="button"
                        class={[
                          'inline-flex items-center gap-1 rounded border px-1.5 text-[10px] font-bold tabular-nums tracking-wider transition-colors',
                          b.color,
                          b.pulse && 'animate-px5-pulse',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        title={b.title}
                        onclick={(e) => {
                          e.stopPropagation();
                          b.onClick?.();
                        }}
                      >
                        {#if b.icon === 'zap'}
                          <Zap
                            class="h-3 w-3 fill-yellow-400 text-yellow-400"
                            strokeWidth={2.5}
                          />
                        {/if}
                        {b.label}
                      </button>
                    {:else}
                      <span
                        class={[
                          'inline-flex items-center gap-1 rounded border px-1.5 text-[10px] font-bold tabular-nums tracking-wider transition-colors',
                          b.color,
                          b.pulse && 'animate-px5-pulse',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        title={b.title}
                      >
                        {#if b.icon === 'zap'}
                          <Zap
                            class="h-3 w-3 fill-yellow-400 text-yellow-400"
                            strokeWidth={2.5}
                          />
                        {/if}
                        {b.label}
                      </span>
                    {/if}
                  {/each}
                </div>
              {/if}
            </div>
          </TableCell>

          <!-- Status -->
          <TableCell class="align-top">
            {#if s.isLive}
              <Badge variant="success" class="animate-live-pulse gap-1">
                <Radio class="size-3" />
                Live
              </Badge>
            {:else}
              <Badge variant="secondary">Closed</Badge>
            {/if}
          </TableCell>

          <!-- Symbol con timeSpan + tooltip de config -->
          <TableCell class="align-top font-mono text-xs">
            {#if tooltipRows.length > 0}
              <Tooltip>
                <TooltipTrigger>
                  {#snippet child({ props })}
                    <span
                      {...props}
                      class="border-muted-foreground/30 cursor-help border-b border-dotted"
                    >
                      {fmtSymbol(s)}
                    </span>
                  {/snippet}
                </TooltipTrigger>
                <TooltipContent class="font-sans">
                  <div class="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
                    {#each tooltipRows as r (r.label)}
                      <span class="text-muted-foreground">{r.label}</span>
                      <span class="font-medium tabular-nums">{r.value}</span>
                    {/each}
                  </div>
                </TooltipContent>
              </Tooltip>
            {:else}
              {fmtSymbol(s)}
            {/if}
          </TableCell>

          <!-- Account normalizado (SIM cuando empieza con 'Sim') -->
          <TableCell class="align-top text-xs">{fmtAccount(s.accountName)}</TableCell>

          <!-- Operator -->
          <TableCell class="align-top text-xs">{s.operatorId}</TableCell>

          <!-- Start time compacto + tooltip con full -->
          <TableCell class="w-[140px] align-top font-mono text-xs">
            <Tooltip>
              <TooltipTrigger>
                {#snippet child({ props })}
                  <span {...props}>{fmtStartTime(s.startTime)}</span>
                {/snippet}
              </TooltipTrigger>
              <TooltipContent class="font-mono">{fmtStartTimeFull(s.startTime)}</TooltipContent>
            </Tooltip>
          </TableCell>

          <!-- P&L real + BT stacked -->
          <TableCell class="align-top text-right tabular-nums">
            <div
              class={[
                s.totalPnL > 0 && 'text-success',
                s.totalPnL < 0 && 'text-destructive',
              ]
                .filter(Boolean)
                .join(' ')}
            >
              {fmtPnL(s.totalPnL)}
            </div>
            {#if s.tradeCount > 0}
              <div
                class="text-muted-foreground/70 text-[10px]"
                title="BT P&L (sin slippage)"
              >
                BT {fmtPnL(s.totalBtPnL)}
              </div>
            {/if}
          </TableCell>

          <!-- Trades -->
          <TableCell class="align-top text-right tabular-nums">{s.tradeCount}</TableCell>

          <!-- Win % -->
          <TableCell class="align-top text-right tabular-nums">
            {s.tradeCount > 0 ? `${s.winRate.toFixed(0)}%` : '—'}
          </TableCell>

          <!-- PF -->
          <TableCell class="align-top text-right tabular-nums">
            {s.tradeCount > 0 ? fmtPF(s.profitFactor) : '—'}
          </TableCell>
        </TableRow>

        {#if isExpanded}
          {@const split = expandedData && !expandedData.loading
            ? splitTrades(expandedData.trades)
            : { open: [], closed: [] }}
          {@const maxDD = computeMaxDrawdown(split.closed)}
          <TableRow class="bg-muted/10 hover:bg-muted/10">
            <!-- TableCell wrapper de shadcn no expone colspan en su type;
                 usamos <td> nativo en este caso puntual para que el panel
                 ocupe el ancho completo (11 columnas). -->
            <td colspan={11} class="p-0">
              {#if expandedData?.loading}
                <div class="flex items-center gap-2 px-4 py-6 text-sm text-muted-foreground">
                  <Loader2 class="size-4 animate-spin" />
                  Loading trades…
                </div>
              {:else if expandedData?.error}
                <div
                  role="alert"
                  class="border-destructive/40 bg-destructive/10 text-destructive m-4 rounded-md border px-4 py-3 text-sm"
                >
                  {expandedData.error}
                </div>
              {:else}
                <SessionDetailPanel
                  tradeCount={s.tradeCount}
                  totalPnL={s.totalPnL}
                  totalBtPnL={s.totalBtPnL}
                  profitFactor={s.profitFactor}
                  winRate={s.winRate}
                  maxDrawdown={maxDD}
                  config={s.config}
                  openTrades={split.open}
                  closedTrades={split.closed}
                  theme="sessions"
                />
              {/if}
            </td>
          </TableRow>
        {/if}
      {/each}
    </TableBody>
  </Table>
</div>

<!-- Parity modals (Fase 6). Vivien fuera del table para evitar nesting raro
     y porque shadcn Dialog usa Portal — el position absoluto no depende del
     parent. parityCheckSessionId / warmupSessionId controlan que session
     muestran. -->
<ParityCheckModal
  bind:open={parityCheckOpen}
  sessionId={parityCheckSessionId}
  onClose={() => {
    parityCheckOpen = false;
  }}
  onOpenWarmup={onOpenWarmupFromCheck}
/>
<WarmupParityModal
  bind:open={warmupOpen}
  sessionId={warmupSessionId}
  onClose={onWarmupClose}
  onBack={warmupCameFromCheck ? onBackToCheckFromWarmup : undefined}
/>
